import { Nav } from 'rsuite';
import React, { useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Header } from '../../../common/header.component';
import { ClientInfo } from '../../../common/client-info';
import { Rbac } from '../../../common/Rbac';
import {
  CleanConversationType,
  RbacObject,
} from '../../../../export-types/cleaned-types';
import { useProfile } from '../../../../api/profile';
import { FormattedMessage, useIntl } from 'react-intl';
import { ConversationWindow } from '../../../common/conversation-window';
import { AdminCustomsDeliveryOrderStatusTable } from './admin-customs-delivery-order-status-table.component';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { trpc, RouterInput } from '../../../../api/trpc';
import { CustomsDeliveryOrderRequest } from './customs-delivery-order-request';
import { CustomsDeliveryComment } from './customs-delivery-comment';
import { CustomsDeliveryOrderGoodItem } from './customs-delivery-order-good-item';
import { AlertService } from '../../../../services/alert.service';
import CalculatorCustomsDeliveryBlock, {
  CalculateCustomsDeliveryFormType,
  CalculatedTksData,
} from './calculator-customs-delivery-block';
import CalculationResultCustomsDeliveryBlock from './calculation-result-customs-delivery-block';
import { CustomsDeliveryCalculationTable } from './customs-delivery-calculation-table';
import { AdminCustomsDeliveryOrderActionsBlock } from './admin-customs-delivery-order-actions-block';
import CustomsDeliveryOrderLogsBlock from './customs-delivery-order-logs-block';
import {
  CustomsDeliveryNotesConversationTab,
  CustomsDeliveryNotesConversationTabRef,
} from './customs-delivery-notes-conversation-tab';
import { uploadMediaInputFiles } from '../../../../services/file-service';
import CustomsDeliveryOrderInvoicesBlock from './invoices/customs-delivery-order-invoices-block';

export const AdminCustomsDeliveryOrderViewScreen: React.FC = () => {
  const intl = useIntl();
  const { id } = useParams();
  const { data: user } = useProfile();
  const orderInput = { id: id as string };
  const { data: order, mutate } = useSWR(
    ['customsDelivery.getOne', orderInput],
    () => trpc.customsDelivery.getOne.query(orderInput),
    {
      onSuccess: () => {
        mutateLogs();
      },
    },
  );
  const logsInput = { id: id as string };
  const { mutate: mutateLogs } = useSWR(
    ['customsDelivery.getOrderLogs', logsInput],
    () => trpc.customsDelivery.getOrderLogs.query(logsInput),
  );

  const { trigger: addNote } = useSWRMutation(
    ['customsDeliveryNotes.addNote'],
    (_k, { arg }: { arg: RouterInput['customsDeliveryNotes']['addNote'] }) =>
      trpc.customsDeliveryNotes.addNote.mutate(arg),
  );
  const { trigger: addGood } = useSWRMutation(
    ['customsDelivery.addGoodToOrder'],
    (_k, { arg }: { arg: RouterInput['customsDelivery']['addGoodToOrder'] }) =>
      trpc.customsDelivery.addGoodToOrder.mutate(arg),
  );
  const [selectedData, setSelectedData] =
    useState<CalculateCustomsDeliveryFormType>();
  const [calculatedData, setCalculatedData] = useState<CalculatedTksData>();
  const [isAddNewGood, setIsAddNewGood] = React.useState(false);

  const notesTabRef = useRef<CustomsDeliveryNotesConversationTabRef>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = useMemo<CleanConversationType | 'notes'>(() => {
    const chat = searchParams.get('chat') || 'supplier';

    if (chat === 'notes') {
      return 'notes';
    }

    if (!['supplier', 'client'].includes(chat)) {
      return 'supplier';
    }
    return chat as CleanConversationType;
  }, [searchParams]);

  if (!order || !user) {
    return null;
  }

  return (
    <div className="d-flex flex-column h-100">
      <Header>
        <div className="d-flex flex-row align-items-center">
          <h1 className="me-3 mb-0">
            {intl.formatMessage(
              {
                defaultMessage: 'Заказ на официальную доставку {value}',
                id: 'customsDelivery.label.redemptionOrder',
                description: 'Заголовок на странице',
              },
              {
                value: 'W' + order.internalId,
              },
            )}
          </h1>
        </div>
      </Header>
      <div className="d-flex flex-column flex-lg-row flex-grow-1 gap-4">
        <div className="col flex-grow-1">
          <div className="bg-gray p-3 pb-0 rounded-8 mb-3">
            <div className="fs-18 fw-600 color-gray-450 mb-3">
              <FormattedMessage
                defaultMessage="Статус заказа"
                id="purchaseOrderPage.label.orderStatus"
                description="Заголовок на странице"
              />
            </div>
            <AdminCustomsDeliveryOrderStatusTable order={order} />
          </div>
          <div className="bg-gray p-3 rounded-8">
            <CustomsDeliveryOrderRequest id={order.id} />
          </div>
          <div className="bg-gray p-3 rounded-8 mt-3">
            <ClientInfo user={order.user!} mutate={mutate} />
          </div>
          <Rbac object={RbacObject.CustomsDelivery} action={'read'}>
            <div className=" p-3 rounded-8 mt-3 bg-gray">
              <div className={'fs-16 fw-600 mb-3 '}>Коды ТН ВЭД в заказе</div>
              <div className={'w-100 d-flex flex-column gap-2 mb-3'}>
                {order.goods
                  .filter((good) => good.codeTariff)
                  .map((good) => {
                    return (
                      <CustomsDeliveryOrderGoodItem
                        good={good}
                        customsDeliveryOrderId={order.id}
                        key={good.id}
                        onDelete={() => {
                          mutate();
                          AlertService.success('Товар удален');
                        }}
                        onUpdate={() => {
                          mutate();
                          AlertService.success('Товар изменен');
                        }}
                      />
                    );
                  })}
                {!isAddNewGood &&
                  order.goods.length < 5 &&
                  [
                    'created',
                    'preparing',
                    'codeSelected',
                    'calculationReady',
                    'protectCalculation',
                    'prepareDocs',
                    'prepareInvoice',
                    'declarant',
                    'waitingForPayment',
                    'buyOut',
                    'inWork',
                    'inStock',
                  ].includes(order.status) && (
                    <div className="mx-1 d-flex flex-row justify-content-center">
                      <div
                        className="btn btn-success mright-auto"
                        onClick={() => {
                          setIsAddNewGood(true);
                        }}
                      >
                        Добавить товар
                      </div>
                    </div>
                  )}

                {isAddNewGood && (
                  <div>
                    <div
                      className={
                        'fs-16 fw-600 mb-3  border-top border-gray-200 py-2 d-flex flex-row align-items-center'
                      }
                    >
                      Найдите код ТН ВЭД и добавьте его в заказ
                      <div className={'mleft-auto'}>
                        <button
                          type="button"
                          className="btn "
                          onClick={() => {
                            setIsAddNewGood(false);
                          }}
                        >
                          <i className="bi bi-x-circle me-2" />
                        </button>
                      </div>
                    </div>
                    <CalculatorCustomsDeliveryBlock
                      setCalculatedData={setCalculatedData}
                      setSelectedData={setSelectedData}
                    />
                    {calculatedData && selectedData && (
                      <div className={'mt-2'}>
                        <CalculationResultCustomsDeliveryBlock
                          calculationData={calculatedData}
                          submitButtonTitle={'Добавить в заказ'}
                          onSubmit={() => {
                            addGood({
                              customsDeliveryOrderId: order.id,
                              good: {
                                amount: selectedData.amount || undefined,
                                price: selectedData.price,
                                name: selectedData.selectedCode?.name || '',
                                code: selectedData.code,
                                currency: selectedData.currency,
                                costs: {
                                  fee: calculatedData.fee,
                                  tolls: calculatedData.tolls,
                                  vat: calculatedData.nds,
                                },
                                sumCost: calculatedData.total,
                                vat: selectedData.vat.value,
                              },
                            }).then(() => {
                              AlertService.success('Товар добавлен');
                              setCalculatedData(undefined);
                              setSelectedData(undefined);
                              setIsAddNewGood(false);
                              mutate();
                            });
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {!['created', 'preparing', 'codeSelected'].includes(
              order.status,
            ) && (
              <div className="bg-gray p-3 rounded-8 mt-3">
                <CustomsDeliveryCalculationTable id={order.id} />
              </div>
            )}

            <div className="bg-gray p-3 rounded-8 mt-3">
              <CustomsDeliveryComment id={order.id} />
            </div>
            <div className="bg-gray p-3 rounded-8 mt-3">
              <CustomsDeliveryOrderLogsBlock id={order.id} />
            </div>
          </Rbac>
        </div>
        <div className="col d-flex flex-column">
          <AdminCustomsDeliveryOrderActionsBlock id={order.id} />
          <div className="flex-grow-1 d-flex mb-4 flex-column">
            <Nav
              appearance="tabs"
              activeKey={tab}
              onSelect={(key) => setSearchParams({ chat: key })}
            >
              <Nav.Item eventKey="supplier" className="bg-gray-50">
                <FormattedMessage
                  defaultMessage="Поставщик"
                  id="TransactiosHistrory.lable.supplier"
                  description="Вкладка"
                />
              </Nav.Item>
              <Nav.Item eventKey="client" className="bg-teal-100">
                <FormattedMessage
                  defaultMessage="Клиент"
                  id="clientInfo.label.customer"
                  description="Заголовок на странице"
                />
              </Nav.Item>
              <Nav.Item eventKey="notes" className="bg-yellow-100">
                <FormattedMessage
                  defaultMessage="Заметки"
                  id="clientInfo.label.notes"
                  description="Заголовок на странице"
                />
              </Nav.Item>
            </Nav>
            <ConversationWindow
              id={order.id}
              type="customs"
              internalId={order.internalId}
              client={order.user}
              supplier={order.supplier}
              tab={tab === 'notes' ? 'additional' : tab}
              additionalTab={{
                title: 'Заметки',
                element: (
                  <CustomsDeliveryNotesConversationTab
                    ref={notesTabRef}
                    customsDeliveryOrderId={order.id}
                  />
                ),
                onSubmit: async (data) => {
                  const attachments = await uploadMediaInputFiles(
                    data.attachments,
                  );
                  if (attachments.length === 0 && !data.message) {
                    return;
                  }
                  await addNote({
                    id: order.id,
                    data: {
                      text: data.message,
                      attachments: attachments.map(
                        (attachment) => attachment.file,
                      ),
                    },
                  });
                  notesTabRef.current?.mutateNotes();
                },
              }}
            />
            <div className=" p-3 rounded-8 mt-3">
              <CustomsDeliveryOrderInvoicesBlock id={order.id} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
